// Must stay the first import — see the module's comment (react-dom feature-detects at init)
import './utils/disableReactPerfTracks'
import { startTransition } from 'react'
import { hydrateRoot } from 'react-dom/client'
import { HydratedRouter } from 'react-router/dom'
import { initReactI18next } from 'react-i18next'
import i18next from 'i18next'
import i18n from './i18n'
import Backend from 'i18next-http-backend'
import { createAppEmotionCache } from './emotionCache'
import { sentryService } from 'services/SentryService'
import { logger } from 'services/logger'
import { getUrlParam } from '../utils/UrlUtils'
import { isProduction } from '../utils/utils'
import 'core-js-pure/stable/aggregate-error' // Required for supporting the 'cause' property in error constructor
import 'polyfills/canvas-to-blob' // For old browsers & all Edge versions
import 'polyfills/flat' // For old browsers & all Edge versions
import 'polyfills/replaceAll' // For Safari <13.4
import 'blueimp-canvas-to-blob'
import { installServiceWorkerIfNeeded } from '../serviceWorkerInstallation'
import { EntryProviders } from './EntryProviders'
import { getRootLoaderData } from 'utils/remixClientUtils'
import { configureMobx } from './utils/mobx'
import { scrubHeadTextNodes } from './utils/scrubHeadTextNodes'

configureMobx(window.KEYS.enableMobxSpy)

window.addEventListener('beforeinstallprompt', (event: Event) => {
  event.preventDefault()
})

installServiceWorkerIfNeeded()

async function hydrate() {
  const rootLoaderData = getRootLoaderData()
  const username = rootLoaderData?.user
  logger.init(window.KEYS, username)
  if (window.KEYS) sentryService.initSentry(window.KEYS)
  logger.info(`Entry client loaded`, {
    utm_source: getUrlParam('utm_source'),
  })

  await i18next
    .use(initReactI18next) // Tell i18next to use the react-i18next plugin
    .use(Backend) // Setup your backend
    .init({
      ...i18n, // spread the configuration
      // This function detects the namespaces your routes rendered while SSR use
      lng: rootLoaderData?.language || 'en',
      detection: {
        // Here only enable htmlTag detection, we'll detect the language only
        // server-side, by using the `<html lang>` attribute
        // we can communicate to the client the language detected server-side
        order: ['htmlTag'],
        // Because we only use htmlTag, there's no reason to cache the language
        // on the browser, so we disable it
        caches: [],
      },
    })

  scrubHeadTextNodes()

  // Created once per page load (hydrate runs once), matching the server's per-request cache.
  const emotionCache = createAppEmotionCache()

  startTransition(() => {
    hydrateRoot(
      document,
      <EntryProviders i18n={i18next} emotionCache={emotionCache}>
        <HydratedRouter />
      </EntryProviders>,
      {
        onRecoverableError: (error) => {
          logger.error('Hydration error', error, {
            country: window.KEYS?.ipCountry,
            language: rootLoaderData?.language,
          })

          // Make the tests fail
          if (!isProduction()) {
            throw error
          }
        },
      }
    )
  })
}

if (window.requestIdleCallback) {
  window.requestIdleCallback(hydrate)
} else {
  // Safari doesn't support requestIdleCallback
  // https://caniuse.com/requestidlecallback
  window.setTimeout(hydrate, 1)
}
